// @ts-nocheck
/**
 * ListCallStatsBar, petite barre de stats par liste affichee dans la
 * toolbar Power Hour. Donne un apercu rapide a l'agent du resultat de
 * ses appels pour la liste courante (selectedSource).
 *
 * 4 metriques :
 *  - Connectes  : prospects dont le dernier outcome est 'contacted'
 *                 (humain joint)
 *  - RDV        : prospects avec outcome 'meeting_booked'
 *  - Pas de rep : prospects avec outcome no_answer / not_reachable /
 *                 failed / busy / voicemail
 *  - Nouveau    : prospects jamais appeles dans cette session/total
 *
 * Tous les comptes sont calcules par prospect (un meme numero ne compte
 * qu'une fois selon son dernier outcome) et scopes a la selectedSource
 * + selectedCustomerId (via filteredProspectQueue).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialer } from '@/contexts/DialerContext';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { effectiveLastCall } from '@/services/dialer/dialRules';
import { hasMeetingBooked } from '@/utils/callUtils';

const NO_ANSWER_OUTCOMES = new Set([
  'no_answer',
  'not_reachable',
  'failed',
  'busy',
  'voicemail',
  'canceled',
]);

export default function ListCallStatsBar() {
  const { t } = useTranslation('dialer');
  const d = useDialer() as any;
  const {
    filteredProspectQueue = [],
    latestCallByPhone = {},
    selectedSource,
    sources = [],
  } = d;

  const stats = useMemo(() => {
    let connected = 0;
    let meetings = 0;
    let noAnswer = 0;
    let iosFilter = 0;
    let voicemailLeft = 0;
    let fresh = 0;

    // Limite a la source selectionnee si l'utilisateur a choisi une liste
    // precise. Sinon (selectedSource vide ou unique source), on prend tout.
    const matchSource = (p: { source?: string | null }) => {
      if (sources.length <= 1) return true;
      if (!selectedSource) return true;
      if (selectedSource === 'Revcall Client Dashboard') {
        return !p.source || p.source === selectedSource;
      }
      return p.source === selectedSource;
    };

    // 2026-05-14, dedupe par phone AVANT le comptage. Le backend renvoie
    // parfois plusieurs ProspectQueue rows pour le meme numero (import multiple,
    // sources distinctes), ce qui gonflait artificiellement les compteurs : un
    // RDV book pour un seul prospect comptait x4 si 4 rows partageaient le phone.
    const seenPhones = new Set<string>();
    for (const p of filteredProspectQueue) {
      if (!matchSource(p)) continue;
      const phone = p.prospect_phone || '';
      if (!phone) continue;
      if (seenPhones.has(phone)) continue;
      seenPhones.add(phone);
      const last = effectiveLastCall(p, latestCallByPhone[phone]);
      // 2026-05-13, 'fresh' = prospect qui n'a JAMAIS ete appele (aucune
      // entree dans latestCallByPhone). Les calls en cours / sans outcome
      // ne comptent dans aucun bucket pour ne pas gonfler "Nouveau".
      if (!last) {
        fresh++;
        continue;
      }
      const outcome = last.call_outcome || '';
      // 2026-05-14, "Connecté" = TOUS les outcomes humain (l'agent a parle).
      // meeting_booked compte aussi dans Connecté ET dans RDV (RDV est un
      // sous-ensemble de Connectés). Avant : seul 'contacted' → un humain
      // qualifie en "Pas interesse" ou "Rappel" tombait dans le neant.
      const HUMAN_OUTCOMES = ['contacted', 'meeting_booked', 'callback_requested', 'not_interested', 'interested'];
      if (hasMeetingBooked(last)) {
        meetings++;
        connected++;
      } else if (HUMAN_OUTCOMES.includes(outcome)) {
        connected++;
      } else if (outcome === 'left_voicemail') {
        // A message was actually left: its own status ("Message vocal").
        voicemailLeft++;
      } else if (outcome === 'ios_filter') {
        // #1, « iOS Filter present dans les stats et la barre de statuts ».
        // Compteur propre : l'agregation dans « Pas de reponse » masquerait
        // justement ce que le client demande de voir.
        iosFilter++;
      } else if (NO_ANSWER_OUTCOMES.has(outcome)) {
        noAnswer++;
      }
      // Sinon (outcome null, vide, ou en cours) : on ne compte rien.
    }
    return { connected, meetings, noAnswer, voicemailLeft, iosFilter, fresh };
  }, [filteredProspectQueue, latestCallByPhone, selectedSource, sources.length]);

  const meetingLabel = outcomeLabel('meeting_booked', t);

  // Each status is a column: its name and count on top, its colored segment
  // directly underneath, so a name can never sit over another status's
  // color. Column widths follow the counts, with the label's own width as a
  // floor so a small status stays readable (R15 in docs/XDIAL_V2_PLAN.md).
  // Booked meetings are connected calls: "Connected" counts the others, so
  // each label's number is exactly its segment. iOS Filter keeps its own
  // status (#1). Statuses at zero have no segment and are not shown; calls
  // still in progress have no outcome yet and are not counted.
  const segments = [
    { key: 'meetings', value: stats.meetings, color: '#15803D', label: meetingLabel },
    { key: 'connected', value: stats.connected - stats.meetings, color: 'var(--vf-green, #10b981)', label: t('dialer:listCallStats.connected', { count: stats.connected - stats.meetings }) },
    { key: 'noAnswer', value: stats.noAnswer, color: 'var(--vf-orange, #f59e0b)', label: outcomeLabel('no_answer', t) },
    { key: 'voicemailLeft', value: stats.voicemailLeft, color: '#64748B', label: outcomeLabel('left_voicemail', t) },
    { key: 'iosFilter', value: stats.iosFilter, color: '#C2410C', label: outcomeLabel('ios_filter', t) },
    { key: 'fresh', value: stats.fresh, color: 'var(--vf-text-3, #a1a1aa)', label: t('dialer:listCallStats.new', { count: stats.fresh }) },
  ].filter((seg) => seg.value > 0);
  const total = segments.reduce((n, seg) => n + seg.value, 0);
  if (total === 0) return null;

  return (
    <div
      className="flex items-end text-[12px]"
      // Grows up to 520px and gives way when the filters unfold on the same row.
      style={{ flex: '1 1 280px', maxWidth: 520, minWidth: 0, gap: 2, color: 'var(--vf-text-2)' }}
      role="img"
      aria-label={segments.map((seg) => `${seg.value} ${seg.label}`).join(', ')}
    >
      {segments.map((seg, i) => (
        <div
          key={seg.key}
          className="flex flex-col gap-1"
          style={{ flexGrow: seg.value, flexBasis: 0, minWidth: 'max-content' }}
          title={`${seg.value} ${seg.label} (${Math.round((seg.value / total) * 100)} %)`}
        >
          <span style={{ paddingRight: 10 }}>
            <Pill dotColor={seg.color} label={seg.label} value={seg.value} />
          </span>
          <span
            style={{
              height: 4,
              background: seg.color,
              borderTopLeftRadius: i === 0 ? 999 : 0,
              borderBottomLeftRadius: i === 0 ? 999 : 0,
              borderTopRightRadius: i === segments.length - 1 ? 999 : 0,
              borderBottomRightRadius: i === segments.length - 1 ? 999 : 0,
            }}
          />
        </div>
      ))}
    </div>
  );
}

function Pill({ dotColor, label, value }: { dotColor: string; label: string; value: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: dotColor,
          flexShrink: 0,
        }}
      />
      <span style={{ color: 'var(--vf-text-1)', fontWeight: 600 }}>{value}</span>
      <span>{label}</span>
    </span>
  );
}
